import {
  Processor,
  LoanMetric,
  Bottleneck,
  PipelineStage,
  CallMetric,
  Condition,
  Alert,
  Loan,
} from '../types/dashboard'

export const processors: Processor[] = [
  {
    id: '1',
    name: 'Sarah Chen',
    avatar: 'https://i.pravatar.cc/150?u=1',
    activeLoans: 24,
    capacity: 85,
    efficiency: 94,
    status: 'active',
    specialty: 'Purchase',
  },
  {
    id: '2',
    name: 'Mike Ross',
    avatar: 'https://i.pravatar.cc/150?u=2',
    activeLoans: 18,
    capacity: 60,
    efficiency: 88,
    status: 'active',
    specialty: 'Refinance',
  },
  {
    id: '3',
    name: 'Jessica Pearson',
    avatar: 'https://i.pravatar.cc/150?u=3',
    activeLoans: 28,
    capacity: 95,
    efficiency: 98,
    status: 'away',
    specialty: 'Jumbo',
  },
  {
    id: '4',
    name: 'Louis Litt',
    avatar: 'https://i.pravatar.cc/150?u=4',
    activeLoans: 12,
    capacity: 40,
    efficiency: 75,
    status: 'active',
    specialty: 'FHA',
  },
  {
    id: '5',
    name: 'Rachel Zane',
    avatar: 'https://i.pravatar.cc/150?u=5',
    activeLoans: 22,
    capacity: 78,
    efficiency: 92,
    status: 'offline',
    specialty: 'Purchase',
  },
  {
    id: '6',
    name: 'Donna Paulsen',
    avatar: 'https://i.pravatar.cc/150?u=6',
    activeLoans: 26,
    capacity: 90,
    efficiency: 99,
    status: 'active',
    specialty: 'Jumbo',
  },
]

export const metrics: LoanMetric[] = [
  {
    id: '1',
    label: 'Total Pipeline Value',
    value: '$42.5M',
    trend: 12,
    trendDirection: 'up',
    status: 'good',
  },
  {
    id: '2',
    label: 'Avg. Turn Time',
    value: '18 Days',
    trend: -5,
    trendDirection: 'down',
    status: 'good',
  }, // Down is good for time
  {
    id: '3',
    label: 'Conditions Pending',
    value: '142',
    trend: 8,
    trendDirection: 'up',
    status: 'warning',
  },
  {
    id: '4',
    label: 'Clear to Close',
    value: '15',
    trend: 25,
    trendDirection: 'up',
    status: 'good',
  },
]

export const bottlenecks: Bottleneck[] = [
  {
    id: '1',
    loanId: 'LN-2024-889',
    stage: 'Underwriting',
    duration: '4 days',
    impact: 'high',
    aiSuggestion: 'Re-assign to Senior Underwriter for expedited review',
    processorId: '2',
  },
  {
    id: '2',
    loanId: 'LN-2024-902',
    stage: 'Appraisal',
    duration: '7 days',
    impact: 'medium',
    aiSuggestion: 'Trigger automated follow-up with AMC vendor',
    processorId: '4',
  },
  {
    id: '3',
    loanId: 'LN-2024-771',
    stage: 'Conditions',
    duration: '3 days',
    impact: 'medium',
    aiSuggestion: 'Schedule call with borrower to clarify income docs',
    processorId: '1',
  },
]

export const pipelineStages: PipelineStage[] = [
  {
    name: 'Application',
    count: 45,
    value: '$15.2M',
    avgDays: 2,
    color: '#3b82f6',
  },
  {
    name: 'Processing',
    count: 38,
    value: '$12.8M',
    avgDays: 12,
    color: '#8b5cf6',
  },
  {
    name: 'Underwriting',
    count: 22,
    value: '$8.5M',
    avgDays: 5,
    color: '#f59e0b',
  },
  { name: 'Closing', count: 15, value: '$6.0M', avgDays: 3, color: '#10b981' },
]

export const callData: CallMetric[] = [
  { time: '09:00', inbound: 12, outbound: 8, missed: 1 },
  { time: '10:00', inbound: 25, outbound: 15, missed: 2 },
  { time: '11:00', inbound: 32, outbound: 22, missed: 0 },
  { time: '12:00', inbound: 18, outbound: 10, missed: 3 },
  { time: '13:00', inbound: 28, outbound: 25, missed: 1 },
  { time: '14:00', inbound: 35, outbound: 30, missed: 0 },
  { time: '15:00', inbound: 30, outbound: 28, missed: 1 },
]

export const conditions: Condition[] = [
  {
    id: '1',
    name: 'W2 Verification',
    category: 'Income',
    aging: 2,
    status: 'Pending',
    loanId: 'LN-889',
    processorId: '1',
  },
  {
    id: '2',
    name: 'Bank Statements',
    category: 'Assets',
    aging: 5,
    status: 'Review',
    loanId: 'LN-902',
    processorId: '2',
  },
  {
    id: '3',
    name: 'Appraisal Report',
    category: 'Property',
    aging: 1,
    status: 'Pending',
    loanId: 'LN-771',
    processorId: '3',
  },
  {
    id: '4',
    name: 'Credit Explanation',
    category: 'Credit',
    aging: 8,
    status: 'Pending',
    loanId: 'LN-654',
    processorId: '4',
  },
  {
    id: '5',
    name: 'Gift Letter',
    category: 'Assets',
    aging: 3,
    status: 'Cleared',
    loanId: 'LN-321',
    processorId: '1',
  },
]

export const alerts: Alert[] = [
  {
    id: '1',
    message: 'Rate lock expiring for 3 loans in < 24h',
    type: 'critical',
    timestamp: '10 min ago',
  },
  {
    id: '2',
    message: 'Unusual volume spike in intake queue',
    type: 'warning',
    timestamp: '35 min ago',
  },
]

export const loans: Loan[] = [
  // Sarah Chen's loans (processor 1)
  {
    id: 'LN-2024-889',
    borrowerName: 'John & Mary Smith',
    loanAmount: 425000,
    stage: 'Underwriting',
    status: 'At Risk',
    daysInStage: 8,
    propertyAddress: '123 Oak St, Austin TX',
    loanType: 'Purchase',
    closingDate: '2024-02-15',
    pendingConditions: 3,
    processorId: '1',
  },
  {
    id: 'LN-2024-771',
    borrowerName: 'Robert Johnson',
    loanAmount: 380000,
    stage: 'Processing',
    status: 'On Track',
    daysInStage: 5,
    propertyAddress: '456 Elm Ave, Dallas TX',
    loanType: 'Purchase',
    closingDate: '2024-02-20',
    pendingConditions: 1,
    processorId: '1',
  },
  {
    id: 'LN-2024-654',
    borrowerName: 'Lisa Anderson',
    loanAmount: 520000,
    stage: 'Closing',
    status: 'Clear to Close',
    daysInStage: 2,
    propertyAddress: '789 Pine Rd, Houston TX',
    loanType: 'Purchase',
    closingDate: '2024-02-10',
    pendingConditions: 0,
    processorId: '1',
  },
  {
    id: 'LN-2024-543',
    borrowerName: 'Michael Brown',
    loanAmount: 295000,
    stage: 'Application',
    status: 'On Track',
    daysInStage: 1,
    propertyAddress: '321 Maple Dr, San Antonio TX',
    loanType: 'Purchase',
    closingDate: '2024-03-01',
    pendingConditions: 5,
    processorId: '1',
  },
  {
    id: 'LN-2024-432',
    borrowerName: 'Jennifer Davis',
    loanAmount: 475000,
    stage: 'Processing',
    status: 'On Track',
    daysInStage: 6,
    propertyAddress: '654 Cedar Ln, Austin TX',
    loanType: 'Purchase',
    closingDate: '2024-02-25',
    pendingConditions: 2,
    processorId: '1',
  },

  // Mike Ross's loans (processor 2)
  {
    id: 'LN-2024-902',
    borrowerName: 'David Wilson',
    loanAmount: 340000,
    stage: 'Processing',
    status: 'Delayed',
    daysInStage: 12,
    propertyAddress: '987 Birch St, Fort Worth TX',
    loanType: 'Refinance',
    closingDate: '2024-02-18',
    pendingConditions: 4,
    processorId: '2',
  },
  {
    id: 'LN-2024-821',
    borrowerName: 'Sarah Martinez',
    loanAmount: 285000,
    stage: 'Underwriting',
    status: 'On Track',
    daysInStage: 4,
    propertyAddress: '147 Spruce Ave, Plano TX',
    loanType: 'Refinance',
    closingDate: '2024-02-22',
    pendingConditions: 2,
    processorId: '2',
  },
  {
    id: 'LN-2024-765',
    borrowerName: 'James Taylor',
    loanAmount: 410000,
    stage: 'Processing',
    status: 'At Risk',
    daysInStage: 9,
    propertyAddress: '258 Willow Ct, Irving TX',
    loanType: 'Refinance',
    closingDate: '2024-02-28',
    pendingConditions: 3,
    processorId: '2',
  },
  {
    id: 'LN-2024-698',
    borrowerName: 'Emily White',
    loanAmount: 325000,
    stage: 'Application',
    status: 'On Track',
    daysInStage: 2,
    propertyAddress: '369 Ash Blvd, Frisco TX',
    loanType: 'Refinance',
    closingDate: '2024-03-05',
    pendingConditions: 6,
    processorId: '2',
  },

  // Jessica Pearson's loans (processor 3)
  {
    id: 'LN-2024-987',
    borrowerName: 'Christopher Lee',
    loanAmount: 1250000,
    stage: 'Underwriting',
    status: 'On Track',
    daysInStage: 6,
    propertyAddress: '741 Highland Park, Dallas TX',
    loanType: 'Jumbo',
    closingDate: '2024-02-12',
    pendingConditions: 2,
    processorId: '3',
  },
  {
    id: 'LN-2024-876',
    borrowerName: 'Amanda Garcia',
    loanAmount: 980000,
    stage: 'Processing',
    status: 'On Track',
    daysInStage: 7,
    propertyAddress: '852 River Oaks Dr, Houston TX',
    loanType: 'Jumbo',
    closingDate: '2024-02-16',
    pendingConditions: 3,
    processorId: '3',
  },
  {
    id: 'LN-2024-765',
    borrowerName: 'Daniel Rodriguez',
    loanAmount: 1450000,
    stage: 'Closing',
    status: 'Clear to Close',
    daysInStage: 1,
    propertyAddress: '963 Preston Hollow, Dallas TX',
    loanType: 'Jumbo',
    closingDate: '2024-02-08',
    pendingConditions: 0,
    processorId: '3',
  },
  {
    id: 'LN-2024-654',
    borrowerName: 'Michelle Thomas',
    loanAmount: 875000,
    stage: 'Application',
    status: 'On Track',
    daysInStage: 3,
    propertyAddress: '159 Westlake Hills, Austin TX',
    loanType: 'Jumbo',
    closingDate: '2024-02-24',
    pendingConditions: 4,
    processorId: '3',
  },

  // Louis Litt's loans (processor 4)
  {
    id: 'LN-2024-543',
    borrowerName: 'Kevin Harris',
    loanAmount: 245000,
    stage: 'Processing',
    status: 'On Track',
    daysInStage: 5,
    propertyAddress: '753 Oak Ridge, Arlington TX',
    loanType: 'FHA',
    closingDate: '2024-02-19',
    pendingConditions: 2,
    processorId: '4',
  },
  {
    id: 'LN-2024-432',
    borrowerName: 'Nicole Clark',
    loanAmount: 215000,
    stage: 'Underwriting',
    status: 'At Risk',
    daysInStage: 7,
    propertyAddress: '864 Meadow View, Grand Prairie TX',
    loanType: 'FHA',
    closingDate: '2024-02-21',
    pendingConditions: 5,
    processorId: '4',
  },
  {
    id: 'LN-2024-321',
    borrowerName: 'Brian Lewis',
    loanAmount: 198000,
    stage: 'Application',
    status: 'On Track',
    daysInStage: 2,
    propertyAddress: '975 Garden Terrace, Garland TX',
    loanType: 'FHA',
    closingDate: '2024-03-02',
    pendingConditions: 3,
    processorId: '4',
  },

  // Rachel Zane's loans (processor 5)
  {
    id: 'LN-2024-210',
    borrowerName: 'Steven Walker',
    loanAmount: 395000,
    stage: 'Processing',
    status: 'On Track',
    daysInStage: 6,
    propertyAddress: '186 Sunset Ridge, Round Rock TX',
    loanType: 'Purchase',
    closingDate: '2024-02-17',
    pendingConditions: 2,
    processorId: '5',
  },
  {
    id: 'LN-2024-198',
    borrowerName: 'Karen Hall',
    loanAmount: 445000,
    stage: 'Underwriting',
    status: 'On Track',
    daysInStage: 4,
    propertyAddress: '297 Lake View, Pflugerville TX',
    loanType: 'Purchase',
    closingDate: '2024-02-23',
    pendingConditions: 1,
    processorId: '5',
  },
  {
    id: 'LN-2024-187',
    borrowerName: 'Paul Allen',
    loanAmount: 365000,
    stage: 'Closing',
    status: 'Clear to Close',
    daysInStage: 1,
    propertyAddress: '308 Hill Country, Cedar Park TX',
    loanType: 'Purchase',
    closingDate: '2024-02-09',
    pendingConditions: 0,
    processorId: '5',
  },

  // Donna Paulsen's loans (processor 6)
  {
    id: 'LN-2024-176',
    borrowerName: 'Laura Young',
    loanAmount: 1125000,
    stage: 'Processing',
    status: 'On Track',
    daysInStage: 8,
    propertyAddress: '419 Tarrytown, Austin TX',
    loanType: 'Jumbo',
    closingDate: '2024-02-14',
    pendingConditions: 3,
    processorId: '6',
  },
  {
    id: 'LN-2024-165',
    borrowerName: 'Mark King',
    loanAmount: 925000,
    stage: 'Underwriting',
    status: 'At Risk',
    daysInStage: 9,
    propertyAddress: '520 Memorial Park, Houston TX',
    loanType: 'Jumbo',
    closingDate: '2024-02-26',
    pendingConditions: 4,
    processorId: '6',
  },
  {
    id: 'LN-2024-154',
    borrowerName: 'Patricia Wright',
    loanAmount: 1350000,
    stage: 'Application',
    status: 'On Track',
    daysInStage: 2,
    propertyAddress: '631 Tanglewood, Houston TX',
    loanType: 'Jumbo',
    closingDate: '2024-03-04',
    pendingConditions: 5,
    processorId: '6',
  },
]
